import React, { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { uploadContactPhoto } from '../../../lib/supabase';
import {
  Area,
  DecodedPhoto,
  FRAME,
  PhotoError,
  PhotoProblem,
  blobToDataUrl,
  checkPickedFile,
  classifyUploadError,
  croppedFile,
  decodePhoto,
  renderCrop,
} from '../../../lib/contactPhoto';
import { PhoneButtonPreview } from './PhoneButtonPreview';
import { PhotoCropStep, SaveStatus } from './PhotoCropStep';
import { useContactPhotoText } from './contactPhotoText';

interface ContactPhotoPickerProps {
  photoUrl?: string;
  contactName: string;
  /**
   * Called with the new public URL after a crop is saved, or null when the
   * photo is removed. The picker never deletes stored files: the old file may
   * still be what the phone shows until the parent saves, so deleting it is
   * the parent's job, after its own save succeeds.
   */
  onChange: (photoUrl: string | null) => void;
  /** Width of the tappable thumbnail, in CSS px. */
  thumbnailWidth?: number;
  disabled?: boolean;
  /**
   * Sign-up: she has no account yet and nobody may upload before signing in.
   * The crop is handed to onChange as a data: URL and uploaded after the
   * account is created (lib/pendingContactPhotos). Nothing is sent here.
   */
  keepOnDevice?: boolean;
}

// "Take a picture" opens the camera only where a camera input exists — phones
// and tablets. On a desktop it would just open the same file dialog twice.
function hasTouchCamera(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
}

/**
 * Contact photo, modelled on Google's "Change profile picture": tap the photo
 * (or the empty placeholder) → a sheet shows the photo large, as the phone
 * button, with "Upload from device" and "Take a picture" → the crop step,
 * which cannot be skipped → only the crop is uploaded.
 */
export const ContactPhotoPicker: FC<ContactPhotoPickerProps> = ({
  photoUrl,
  contactName,
  onChange,
  thumbnailWidth = 64,
  disabled = false,
  keepOnDevice = false,
}) => {
  const text = useContactPhotoText();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [pickProblem, setPickProblem] = useState<PhotoProblem | null>(null);
  const [photo, setPhoto] = useState<DecodedPhoto | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const showCamera = hasTouchCamera();

  const closeCrop = () => {
    setPhoto(null);
    setStatus('idle');
  };

  // Escape closes the innermost layer only. Capture phase, so the drawer
  // behind us does not also close on the same key press.
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (status === 'saving') return;
      if (photo) closeCrop();
      else setSheetOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [sheetOpen, photo, status]);

  useEffect(() => {
    if (sheetOpen && !photo) firstActionRef.current?.focus();
  }, [sheetOpen, photo]);

  const openSheet = () => {
    if (disabled) return;
    setPickProblem(null);
    setSheetOpen(true);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so picking the same file again still fires
    if (!file) return;

    closeCrop();
    setPickProblem(null);
    const problem = checkPickedFile(file);
    if (problem) {
      setPickProblem(problem);
      return;
    }
    setOpening(true);
    try {
      const decoded = await decodePhoto(file);
      setStatus('idle');
      setPhoto(decoded);
    } catch (err) {
      console.error('Contact photo could not be opened:', err);
      setPickProblem(err instanceof PhotoError ? err.problem : 'cannotOpen');
    } finally {
      setOpening(false);
    }
  };

  const handleConfirm = async (area: Area) => {
    if (!photo) return;
    setStatus('saving');
    let blob: Blob;
    try {
      blob = await renderCrop(photo.image, area);
    } catch (err) {
      console.error('Contact photo crop could not be prepared:', err);
      setStatus('unprepared');
      return;
    }
    try {
      const url = keepOnDevice ? await blobToDataUrl(blob) : await uploadContactPhoto(croppedFile(blob));
      onChange(url);
      closeCrop();
    } catch (err) {
      console.error('Contact photo upload failed:', err);
      setStatus(keepOnDevice ? 'unprepared' : classifyUploadError(err) === 'uploadRefused' ? 'refused' : 'failed');
    }
  };

  const handleRemove = () => {
    onChange(null);
    setPickProblem(null);
  };

  const thumbHeight = Math.round(thumbnailWidth * (FRAME.height / FRAME.width));
  const triggerLabel = photoUrl ? text('changePhoto') : text('addPhoto');

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        disabled={disabled}
        aria-label={`${triggerLabel}${contactName ? ` · ${contactName}` : ''}`}
        data-testid="contact-photo-trigger"
        className="shrink-0 overflow-hidden flex items-center justify-center bg-brand-bg-warm border border-brand-border hover:border-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-50"
        style={{ width: thumbnailWidth, height: thumbHeight, borderRadius: Math.max(6, (16 * thumbnailWidth) / FRAME.width) }}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="material-symbols-outlined text-brand-text-muted" aria-hidden="true">
            add_a_photo
          </span>
        )}
      </button>

      {sheetOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60] bg-black/50" onClick={() => !opening && setSheetOpen(false)} />
            <div
              role="dialog"
              aria-modal="true"
              aria-label={text('sheetTitle')}
              className="fixed z-[60] inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[400px] bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[95vh] overflow-y-auto"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="flex items-center justify-between px-4 pt-3">
                <h2 className="font-display text-lg font-bold text-brand-text dark:text-white">{text('sheetTitle')}</h2>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  aria-label={text('close')}
                  className="w-12 h-12 -mr-2 rounded-full flex items-center justify-center text-brand-text-muted hover:bg-brand-bg-warm"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <div className="flex flex-col items-center px-4 pb-4">
                <PhoneButtonPreview
                  name={contactName || text('noName')}
                  photoUrl={photoUrl}
                  width={Math.min(FRAME.width * 1.25, 230)}
                  placeholderText={text('noPhoto')}
                  alt={text('photoOf', { name: contactName })}
                />
                <p className="text-sm text-brand-text-muted mt-2 text-center">{text('sheetHint')}</p>

                {pickProblem && (
                  <p role="alert" className="w-full mt-3 p-3 rounded-xl bg-red-50 border border-red-200 text-base text-red-700">
                    {text(pickProblem)}
                  </p>
                )}

                {opening ? (
                  <p className="mt-4 flex items-center gap-2 text-base text-brand-text-light" role="status">
                    <span className="material-symbols-outlined animate-spin" aria-hidden="true">progress_activity</span>
                    {text('opening')}
                  </p>
                ) : (
                  <div className="w-full mt-4 flex flex-col gap-2">
                    <button
                      ref={firstActionRef}
                      type="button"
                      onClick={() => uploadInputRef.current?.click()}
                      className="w-full min-h-[52px] px-4 rounded-xl bg-brand-primary text-white font-bold text-base flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">photo_library</span>
                      {text('uploadFromDevice')}
                    </button>
                    {showCamera && (
                      <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        className="w-full min-h-[52px] px-4 rounded-xl border-2 border-brand-primary text-brand-primary font-bold text-base flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">photo_camera</span>
                        {text('takePicture')}
                      </button>
                    )}
                    {photoUrl && (
                      <button
                        type="button"
                        onClick={handleRemove}
                        className="w-full min-h-[48px] px-4 rounded-xl text-red-700 font-semibold text-base flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                        {text('removePhoto')}
                      </button>
                    )}
                    {photoUrl && (
                      <button
                        type="button"
                        onClick={() => setSheetOpen(false)}
                        className="w-full min-h-[48px] px-4 rounded-xl text-brand-text-light font-semibold text-base"
                      >
                        {text('done')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* image/* (not a list of types) lets the phone use its own photo picker,
                  and lets an iPhone hand over a JPEG copy of a HEIC photo. */}
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="contact-photo-upload-input"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFile}
                className="hidden"
                data-testid="contact-photo-camera-input"
              />
            </div>

            {photo && (
              <PhotoCropStep
                photo={photo}
                contactName={contactName}
                status={status}
                onConfirm={handleConfirm}
                onCancel={closeCrop}
                onChooseAnother={() => uploadInputRef.current?.click()}
              />
            )}
          </>,
          document.body,
        )}
    </>
  );
};

export default ContactPhotoPicker;
